import React, { useEffect } from 'react';
import { Skeleton, Row, Col, Card } from 'antd';
import { Formik } from 'formik';

import { useBreadcrumbContext } from '../../../../../domain/utility/AppContextProvider/BreadcrumbContextProvider';
import IntlMessages from '../../../../../domain/utility/IntlMessages';
import MainUtils from '../../../../../shared/utils/main';
import { useNavigate, useParams } from 'react-router-dom';
import ButtonComponent from '../../../../../shared/components/button';
import { useIntl } from 'react-intl';
import LanguagesTab from '../../../../../shared/components/languages-tab';
import LangComponent from './lang-component';
import { IoEarthOutline } from 'react-icons/io5';
import { schema } from './schema';
import LanguageCode from '../../../../../shared/services/types/language-code';
import { useGetCountryById } from '../../hooks/useGetCountryById';
import { Switch, Input } from 'formik-antd';
import FormItem from '../../../../../shared/components/form-item';
import { ICreateCountry } from '../../../../../shared/services/configuration/countries/dtos/create-country';
import { useCreateCountry } from '../../hooks/useCreateCountry';
import { useEditCountry } from '../../hooks/useEditCountry';
import { validationInputNumber } from 'src/shared/functions/validation-input-number';
import { BsFillArrowLeftCircleFill } from 'react-icons/bs';
import { useLocaleContext } from 'src/domain/utility/AppContextProvider/LocaleContextProvide';
import { getLocaleValue } from 'src/shared/functions/change-locales';

function CountryInfo() {
  const { setBreadcrumb }: any = useBreadcrumbContext();
  const { id } = useParams();
  const { messages } = useIntl();
  const navigate = useNavigate();
  const { locale }: any = useLocaleContext();

  useEffect(() => {
    setBreadcrumb([
      {
        text: <IntlMessages id='configuration.sideBarName' />,
        url: '/configuration/general-configuration',
      },
      {
        text: <IntlMessages id='configuration.countries' />,
        url: '/configuration/countries',
      },
      MainUtils.isEmptyValue(id)
        ? {
          text: <IntlMessages id='countries.add' />,
          url: '/configuration/countries/create',
        }
        : {
          text: <IntlMessages id='countries.edit' />,
          url: `/configuration/countries/edit/${id}`,
        },
    ]);
  }, [id]);

  const { mutate: getByID, isLoading: getLoading, data } = useGetCountryById();
  const { mutate: create, isLoading: createLoading } = useCreateCountry();
  const { mutate: edit, isLoading: editLoading } = useEditCountry();

  useEffect(() => {
    if (id) getByID(id);
  }, [id]);

  const Language = () => [
    {
      code: LanguageCode.English,
      langComponent: <LangComponent code={LanguageCode.English} index={0} />,
    },
    {
      code: LanguageCode.Español,
      langComponent: <LangComponent code={LanguageCode.Español} index={1} />,
    },
    {
      code: LanguageCode.Arabic,
      langComponent: <LangComponent code={LanguageCode.Arabic} index={2} />,
    },
  ];

  const handleSubmit = (values: ICreateCountry) => {
    if (id) {
      const newValues = {
        ...values,
        id: id,
        name: !MainUtils.isEmptyValue(
          getLocaleValue(values?.locales, LanguageCode.English, 'name'),
        )
          ? getLocaleValue(values?.locales, LanguageCode.English, 'name')
          : getLocaleValue(values?.locales, LanguageCode.Arabic, 'name'),
      };
      edit(newValues);
    } else create(values);
  };

  return (
    <Card>
      <Formik
        initialValues={{
          locales:
            data?.Locales?.map((item) => {
              return {
                id: item.Id,
                languageId: item.LanguageId,
                localeKey: item.LocaleKey,
                localeValue: item.LocaleValue,
              };
            }) ?? [],
          name: data?.Name ?? '',
          twoLetterIsoCode: data?.TwoLetterIsoCode ?? '',
          threeLetterIsoCode: data?.ThreeLetterIsoCode ?? '',
          numericIsoCode: data?.NumericIsoCode ?? '',
          subjectToVat: data?.SubjectToVat ?? false,
          published: data?.Published ?? false,
          displayOrder: data?.DisplayOrder ?? 0,
          allowsShipping: data?.AllowsShipping ?? false,
          allowsBilling: data?.AllowsBilling ?? false,
        }}
        validationSchema={schema(messages)}
        validateOnChange={false}
        validateOnBlur={false}
        enableReinitialize
        onSubmit={(values: any) => handleSubmit(values)}>
        {(formik) => (
          <Row gutter={[0, 25]}>
            <Skeleton active loading={getLoading}>
              <Col span={24}>
                <Row justify='space-between' className='fullContent'>
                  <Col>
                    <div className='text-title'>
                      <span className='icon-title'>
                        <IoEarthOutline />
                      </span>
                      {MainUtils.isEmptyValue(id) ? (
                        <IntlMessages id='countries.add' />
                      ) : (
                        `${messages['countries.edit'].toString()} - ${data?.Locales?.find(
                          (item) => item.LanguageId === locale.code,
                        )?.LocaleValue ?? data?.Name
                        }`
                      )}
                      <small className='back-to-list'>
                        <BsFillArrowLeftCircleFill className='back-to-icon' />
                        <a
                          onClick={() => {
                            navigate('/configuration/countries');
                          }}>
                          <IntlMessages id='countries.backTo' />
                        </a>
                      </small>
                    </div>
                  </Col>
                  <Col>
                    <ButtonComponent
                      onClickAction={formik.handleSubmit}
                      type='primary'
                      loading={createLoading || editLoading}
                      text={
                        MainUtils.isEmptyValue(id)
                          ? messages['countries.add'].toString()
                          : messages['countries.edit'].toString()
                      }
                      size='small'
                      className='add-button'
                    />
                  </Col>
                </Row>
              </Col>
              <Col span={24}>
                <div className='languages-tab'>
                  <LanguagesTab
                    languagesList={Language()}
                    tabItem={{
                      key: 'Standard',
                      label: `${messages[
                        'category.categoryInfo.Standard'
                      ].toString()}`,
                      children: (
                        <LangComponent
                          code={`${!MainUtils.isEmptyValue(locale.code)
                            ? locale.code
                            : LanguageCode.English
                            }`}
                        />
                      ),
                    }}
                  />
                </div>
              </Col>
              <Col span={24}>
                <FormItem
                  name='allowsBilling'
                  label={messages['countries.AllowsBilling'].toString()}
                  fieldCommponent={
                    <Switch
                      name='allowsBilling'
                      checkedChildren={<IntlMessages id='common.active' />}
                      unCheckedChildren={<IntlMessages id='common.inactive' />}
                    />
                  }
                />
              </Col>
              <Col span={24}>
                <FormItem
                  name='allowsShipping'
                  label={messages['countries.AllowsShipping'].toString()}
                  fieldCommponent={
                    <Switch
                      name='allowsShipping'
                      checkedChildren={<IntlMessages id='common.active' />}
                      unCheckedChildren={<IntlMessages id='common.inactive' />}
                    />
                  }
                />
              </Col>
              <Col span={24}>
                <FormItem
                  name='twoLetterIsoCode'
                  label={messages['countries.TwoLetterIsoCode'].toString()}
                  fieldCommponent={<Input name='twoLetterIsoCode' />}
                />
              </Col>
              <Col span={24}>
                <FormItem
                  name='threeLetterIsoCode'
                  label={messages['countries.ThreeLetterIsoCode'].toString()}
                  fieldCommponent={<Input name='threeLetterIsoCode' />}
                />
              </Col>
              <Col span={24}>
                <FormItem
                  name='numericIsoCode'
                  label={messages['countries.NumericIsoCode'].toString()}
                  fieldCommponent={
                    <Input
                      style={{ width: '100%' }}
                      name='numericIsoCode'
                      onChange={(e) => {
                        if (validationInputNumber(e.target.value)) {
                          formik.setFieldValue(
                            'numericIsoCode',
                            e.target.value,
                          );
                        } else formik.setFieldValue('numericIsoCode', '');
                      }}
                    />
                  }
                />
              </Col>
              <Col span={24}>
                <FormItem
                  name='subjectToVat'
                  label={messages['countries.SubjectToVat'].toString()}
                  fieldCommponent={
                    <Switch
                      name='subjectToVat'
                      checkedChildren={<IntlMessages id='common.active' />}
                      unCheckedChildren={<IntlMessages id='common.inactive' />}
                    />
                  }
                />
              </Col>
              <Col span={24}>
                <FormItem
                  name='published'
                  label={messages['countries.Published'].toString()}
                  fieldCommponent={
                    <Switch
                      name='published'
                      checkedChildren={<IntlMessages id='common.active' />}
                      unCheckedChildren={<IntlMessages id='common.inactive' />}
                    />
                  }
                />
              </Col>
              <Col span={24}>
                <FormItem
                  name='displayOrder'
                  label={messages['countries.DisplayOrder'].toString()}
                  fieldCommponent={
                    <Input
                      style={{ width: '100%' }}
                      name='displayOrder'
                      onChange={(e) => {
                        if (validationInputNumber(e.target.value)) {
                          formik.setFieldValue(
                            'displayOrder',
                            Number(e.target.value),
                          );
                        } else formik.setFieldValue('displayOrder', 0);
                      }}
                    />
                  }
                />
              </Col>
            </Skeleton>
          </Row>
        )}
      </Formik>
    </Card>
  );
}

export default CountryInfo;
